import type { TimeEntry } from '~/types/timeEntry.type'
import { subject } from '@casl/ability'
import { unknownError } from '~/constants'
import { timeEntriesEventsSourceId } from '~/types/calendar.type'

export const useTimeEntryActionsStore = defineStore('time-entry-actions', () => {
  const toast = useToast()

  const timeEntryStore = useTimeEntries()
  const { timeEntries } = storeToRefs(timeEntryStore)

  const timeTracker = useTimeTracker()
  const { activeCalendar } = storeToRefs(timeTracker)

  const loadingBar = useNaiveLoadingBar()

  const snappedToSeconds = ref<number>()
  const { shift: isSplittingKeyPressed } = useMagicKeys()
  watch(isSplittingKeyPressed, (isPressed) => {
    if (!isPressed)
      snappedToSeconds.value = undefined
  })
  const isSplittingTimeEntry = shallowRef(false)
  const isMergingTimeEntries = shallowRef(false)

  const timeEntryToCreate = shallowRef<ApiTimeEntryCreateParams>({} as ApiTimeEntryCreateParams)

  const {
    data: timeEntryCreateData,
    status: timeEntryCreateStatus,
    error: timeEntryCreateError,
    execute: createTimeEntry,
  } = useApiTimeEntryCreate(timeEntryToCreate, { watch: false })

  const timeEntryIdToUpdate = shallowRef<TimeEntry['id']>(-1)
  const timeEntryUpdateParams = shallowRef<ApiTimeEntryUpdateParams>({} as ApiTimeEntryUpdateParams)

  const {
    data: timeEntryUpdateData,
    status: timeEntryUpdateStatus,
    error: timeEntryUpdateError,
    execute: updateTimeEntry,
  } = useApiTimeEntryUpdateById(timeEntryIdToUpdate, timeEntryUpdateParams, { watch: false })

  const timeEntryIdToDelete = shallowRef<TimeEntry['id']>(-1)

  const {
    status: timeEntryDeleteStatus,
    error: timeEntryDeleteError,
    execute: deleteTimeEntry,
  } = useApiTimeEntryDeleteById(timeEntryIdToDelete, { watch: false })

  const { can } = useAppAbility()

  async function splitTimeEntry(timeEntry: TimeEntry, splitAt: Date) {
    if (timeEntryDeleteStatus.value === 'pending' || timeEntryUpdateStatus.value === 'pending')
      return

    isSplittingTimeEntry.value = true

    loadingBar.start()
    timeEntryIdToUpdate.value = timeEntry.id

    const splitAtDate = snappedToSeconds.value ? new Date(timeEntry.startAt.getTime() + snappedToSeconds.value * 1000) : splitAt

    timeEntryUpdateParams.value = {
      startAt: timeEntry.startAt,
      endAt: splitAtDate,
    }
    await updateTimeEntry()
    snappedToSeconds.value = undefined
    if (timeEntryUpdateData.value) {
      activeCalendar.value?.getApi().getEventById(`${timeEntry.id}`)?.remove()
      timeEntries.value = [...timeEntries.value.filter(otherTimeEntry => timeEntry.id !== otherTimeEntry.id), timeEntryUpdateData.value.data]
      const timeEntrySubject = subject('time-entry', timeEntry)
      timeEntryToCreate.value = {
        startAt: splitAtDate,
        endAt: timeEntry.endAt,
        notes: can('create', timeEntrySubject, 'notes') ? timeEntry.notes : undefined,
        taskId: timeEntry.taskId,
        isBilled: can('create', timeEntrySubject, 'isBilled') ? timeEntry.isBilled : undefined,
        userId: timeEntry.userId,
      }
      activeCalendar.value?.getApi().addEvent({
        id: `${timeEntryUpdateData.value.data.id}`,
        start: timeEntryUpdateData.value.data.startAt,
        end: timeEntryUpdateData.value.data.endAt!,
        timeEntry: timeEntryUpdateData.value.data,
      }, timeEntriesEventsSourceId.description)

      await createTimeEntry()
      if (timeEntryCreateData.value) {
        loadingBar.finish()
        timeEntries.value.push(timeEntryCreateData.value.data)
        activeCalendar.value?.getApi().addEvent({
          id: `${timeEntryCreateData.value.data.id}`,
          start: timeEntryCreateData.value.data.startAt,
          end: timeEntryCreateData.value.data.endAt!,
          timeEntry: timeEntryCreateData.value.data,
        }, timeEntriesEventsSourceId.description)
        toast.add({ color: 'success', description: 'Zeiteintrag wurde erfolgreich aufgeteilt!' })
      }
      else if (timeEntryCreateError.value) {
        loadingBar.error()
        toast.add({ color: 'error', description: timeEntryCreateError.value.data?.message ?? unknownError })
      }
    }
    else if (timeEntryUpdateError.value) {
      loadingBar.error()
      toast.add({ color: 'error', description: timeEntryUpdateError.value.data?.message ?? unknownError })
    }
    isSplittingTimeEntry.value = false
  }

  async function mergeTimeEntries(timeEntry: TimeEntry, timeEntryToMergeWith: TimeEntry) {
    if (timeEntryCreateStatus.value === 'pending' || timeEntryUpdateStatus.value === 'pending')
      return

    loadingBar.start()

    isMergingTimeEntries.value = true
    timeEntryIdToDelete.value = timeEntry.id
    await deleteTimeEntry()

    if (!timeEntryDeleteError.value) {
      activeCalendar.value?.getApi().getEventById(`${timeEntry.id}`)?.remove()
      timeEntries.value = timeEntries.value.filter(otherTimeEntry => timeEntry.id !== otherTimeEntry.id)

      const startAt = timeEntry.startAt < timeEntryToMergeWith.startAt ? timeEntry.startAt : timeEntryToMergeWith.startAt
      const endAt = timeEntry.endAt! > timeEntryToMergeWith.endAt! ? timeEntry.endAt : timeEntryToMergeWith.endAt

      timeEntryIdToUpdate.value = timeEntryToMergeWith.id
      timeEntryUpdateParams.value = {
        startAt,
        endAt,
      }

      await updateTimeEntry()
      if (timeEntryUpdateData.value) {
        timeEntries.value = [...timeEntries.value.filter(otherTimeEntry => timeEntryToMergeWith.id !== otherTimeEntry.id), timeEntryUpdateData.value.data]
        activeCalendar.value?.getApi().getEventById(`${timeEntryToMergeWith.id}`)?.remove()
        activeCalendar.value?.getApi().addEvent({
          id: `${timeEntryUpdateData.value.data.id}`,
          start: timeEntryUpdateData.value.data.startAt,
          end: timeEntryUpdateData.value.data.endAt!,
          timeEntry: timeEntryUpdateData.value.data,
        }, timeEntriesEventsSourceId.description)
        toast.add({ color: 'success', description: 'Zeiteinträge wurden erfolgreich zusammengefügt!' })
      }
      else if (timeEntryUpdateError.value) {
        loadingBar.error()
        toast.add({ color: 'error', description: timeEntryUpdateError.value.data?.message ?? unknownError })
      }
    }
    else {
      loadingBar.error()
      toast.add({ color: 'error', description: timeEntryDeleteError.value.data?.message ?? unknownError })
    }
    isMergingTimeEntries.value = false
  }

  const isTimeEntryCreateLoading = computed(() => timeEntryCreateStatus.value === 'pending')

  const isAnyTimeEntryUpdateLoading = computed(() => timeEntryUpdateStatus.value === 'pending')

  function isTimeEntryUpdateLoading(timeEntryId: TimeEntry['id']) {
    return computed(() => timeEntryIdToUpdate.value === timeEntryId && isAnyTimeEntryUpdateLoading.value)
  }

  const isAnyTimeEntryDeleteLoading = computed(() => timeEntryDeleteStatus.value === 'pending')

  function isTimeEntryDeleteLoading(timeEntryId: TimeEntry['id']) {
    return computed(() => timeEntryIdToDelete.value === timeEntryId && isAnyTimeEntryDeleteLoading.value)
  }

  function isAnyTimeEntryActionLoading(timeEntryId: TimeEntry['id']) {
    return computed(() => isTimeEntryUpdateLoading(timeEntryId).value
      || isTimeEntryDeleteLoading(timeEntryId).value
      || (timeEntryCreateStatus.value === 'pending' && timeEntryIdToUpdate.value === timeEntryId && isSplittingTimeEntry.value))
  }

  return {
    timeEntryToCreate,
    timeEntryIdToUpdate,
    timeEntryUpdateParams,
    timeEntryIdToDelete,
    isSplittingTimeEntry,
    isMergingTimeEntries,
    splitTimeEntry,
    mergeTimeEntries,
    isAnyTimeEntryActionLoading,
    isTimeEntryCreateLoading,
    createTimeEntry,
    timeEntryCreateError,
    timeEntryCreateData,
    isAnyTimeEntryUpdateLoading,
    isTimeEntryUpdateLoading,
    updateTimeEntry,
    timeEntryUpdateError,
    timeEntryUpdateData,
    isAnyTimeEntryDeleteLoading,
    isTimeEntryDeleteLoading,
    deleteTimeEntry,
    timeEntryDeleteError,
    snappedToSeconds,
    isSplittingKeyPressed,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useTimeEntryActionsStore, import.meta.hot))
